import { Header } from "@/components/landing/Header";
import { Hero } from "@/components/landing/Hero";
import { Marquee } from "@/components/landing/Marquee";
import { SmoothScroll } from "@/components/effects/SmoothScroll";
import { ScrollProgress } from "@/components/effects/ScrollProgress";
import { AmbientField } from "@/components/effects/AmbientField";
import { lazy, Suspense, useEffect } from "react";

// Code-split below-the-fold sections to reduce initial JS payload.
const Categories = lazy(() => import("@/components/landing/Categories").then(m => ({ default: m.Categories })));
const SourcingBand = lazy(() => import("@/components/landing/SourcingBand").then(m => ({ default: m.SourcingBand })));
const Membership = lazy(() => import("@/components/landing/Membership").then(m => ({ default: m.Membership })));
const HowItWorks = lazy(() => import("@/components/landing/HowItWorks").then(m => ({ default: m.HowItWorks })));
const Science = lazy(() => import("@/components/landing/Science").then(m => ({ default: m.Science })));
const Spotlight = lazy(() => import("@/components/landing/Spotlight").then(m => ({ default: m.Spotlight })));
const Testimonials = lazy(() => import("@/components/landing/Testimonials").then(m => ({ default: m.Testimonials })));
const Pricing = lazy(() => import("@/components/landing/Pricing").then(m => ({ default: m.Pricing })));
const FAQ = lazy(() => import("@/components/landing/FAQ").then(m => ({ default: m.FAQ })));
const FinalCTA = lazy(() => import("@/components/landing/FinalCTA").then(m => ({ default: m.FinalCTA })));
const Footer = lazy(() => import("@/components/landing/Footer").then(m => ({ default: m.Footer })));
const TrustBar = lazy(() => import("@/components/landing/TrustBar").then(m => ({ default: m.TrustBar })));
const StickyCTA = lazy(() => import("@/components/landing/StickyCTA").then(m => ({ default: m.StickyCTA })));
const Compare = lazy(() => import("@/components/landing/Compare").then(m => ({ default: m.Compare })));
const PressQuote = lazy(() => import("@/components/landing/PressQuote").then(m => ({ default: m.PressQuote })));
const Outcomes = lazy(() => import("@/components/landing/Outcomes").then(m => ({ default: m.Outcomes })));
const MeetClinicians = lazy(() => import("@/components/landing/MeetClinicians").then(m => ({ default: m.MeetClinicians })));
const FounderVideo = lazy(() => import("@/components/landing/FounderVideo").then(m => ({ default: m.FounderVideo })));
const ProductPreview = lazy(() => import("@/components/landing/ProductPreview").then(m => ({ default: m.ProductPreview })));
const WhyOlive = lazy(() => import("@/components/landing/WhyOlive").then(m => ({ default: m.WhyOlive })));
const Unboxing = lazy(() => import("@/components/landing/Unboxing").then(m => ({ default: m.Unboxing })));
const WaitlistModal = lazy(() => import("@/components/landing/WaitlistModal").then(m => ({ default: m.WaitlistModal })));

const Index = () => {
  useEffect(() => {
    const title = "Olive Health — Telehealth membership for whole-body care";
    const description =
      "One membership for GLP-1, peptides, hormones, longevity and whole-body care. US-licensed clinicians, FDA-regulated pharmacies, shipped to your door.";
    document.title = title;

    const setMeta = (selector: string, attr: string, key: string, content: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta(`meta[name="description"]`, "name", "description", description);
    setMeta(`meta[property="og:title"]`, "property", "og:title", title);
    setMeta(`meta[property="og:description"]`, "property", "og:description", description);
    setMeta(`meta[name="twitter:title"]`, "name", "twitter:title", title);
    setMeta(`meta[name="twitter:description"]`, "name", "twitter:description", description);

    const CANONICAL = "https://olivehealth.care/";
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", CANONICAL);
    setMeta(`meta[property="og:url"]`, "property", "og:url", CANONICAL);

    const addLd = (id: string, data: object) => {
      document.getElementById(id)?.remove();
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = id;
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
    };
    addLd("ld-org", {
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      name: "Olive Health",
      description:
        "Telehealth membership for GLP-1, peptides, hormones, longevity, and whole-body care.",
      medicalSpecialty: ["PrimaryCare", "Endocrine"],
      areaServed: "US",
    });
    import("@/components/landing/FAQ").then(({ FAQS }) => {
      addLd("ld-faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    });
  }, []);

  return (
    <main className="relative min-h-screen bg-background">
      <AmbientField />
      <SmoothScroll />
      <ScrollProgress />
      <a
        href="#treatments"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[70] focus:rounded-full focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:text-sm"
      >
        Skip to treatments
      </a>
      <Header />
      <Hero />
      <Marquee />
      <Suspense fallback={null}>
        <WhyOlive />
        <div className="cv-auto"><Categories /></div>
        <SourcingBand />
        <div className="cv-auto"><HowItWorks /></div>
        <div className="cv-auto"><Spotlight /></div>
        <div className="cv-auto"><MeetClinicians /></div>
        <div className="cv-auto"><FounderVideo /></div>
        <div className="cv-auto"><Science /></div>
        <div className="cv-auto"><ProductPreview /></div>
        <div className="cv-auto"><Unboxing /></div>
        <div className="cv-auto"><Outcomes /></div>
        <div className="cv-auto"><Testimonials /></div>
        <div className="cv-auto"><PressQuote /></div>
        <div className="cv-auto"><Membership /></div>
        <div className="cv-auto"><Compare /></div>
        <div className="cv-auto"><Pricing /></div>
        <div className="cv-auto"><FAQ /></div>
        <FinalCTA />
        <TrustBar />
        <Footer />
        <StickyCTA />
        <WaitlistModal />
      </Suspense>
    </main>
  );
};

export default Index;
